/**
 * Rechtefehler der Datenbank in Klartext (Rollensystem, docs/rollensystem-plan.md §11).
 *
 * Die Prüfungen der Migrationen Nr. 151–154 melden sich einheitlich mit
 * `<objekt>: fehlendes Recht <schluessel>` und errcode 42501, etwa
 * `invoices_recht: fehlendes Recht rechnungen.ausstellen`. Daraus wird der
 * Satz, den die Oberfläche zeigt — mit dem Titel aus dem Katalog statt des
 * technischen Schlüssels.
 *
 * Alles andere ist hier kein Rechtefehler, auch eine schlichte RLS-Ablehnung
 * mit 42501 („new row violates row-level security policy“): dann null, und
 * der Aufrufer zeigt seinen bisherigen Text. Ein Fehler OHNE Code zählt mit —
 * viele Dienste reichen nur die Meldung weiter (`new Error(error.message)`),
 * der Code geht dabei verloren.
 */
import { describeError } from '../errorMessage';
import { RECHTE, istRecht, type RechtSchluessel } from './katalog';

/** Der Schlüssel nach „fehlendes Recht“, z. B. `rechnungen.ausstellen`. */
const MUSTER = /fehlendes Recht ([a-z0-9_]+(?:\.[a-z0-9_]+)+)/;

/** Titel eines Rechts aus dem Katalog („Rechnungen ausstellen“). */
export const rechtTitel = (recht: RechtSchluessel): string =>
    RECHTE.find(r => r.schluessel === recht)?.titel ?? recht;

/**
 * „Dafür fehlt Ihnen das Recht „…“.“ für einen Rechtefehler der Datenbank,
 * sonst null.
 */
export const rechtFehlerText = (e: { code?: string; message?: string } | null | undefined): string | null => {
    if (!e || typeof e.message !== 'string') return null;
    if (e.code && e.code !== '42501') return null;
    const treffer = MUSTER.exec(e.message);
    if (!treffer) return null;
    const schluessel = treffer[1];
    // Ein Recht, das dieser Client noch nicht kennt (Datenbank neuer als die
    // App): der Schlüssel sagt immer noch mehr als die rohe Meldung.
    const titel = istRecht(schluessel) ? rechtTitel(schluessel) : schluessel;
    return `Dafür fehlt Ihnen das Recht „${titel}“.`;
};

/**
 * Für `catch (e: unknown)`: der Satz zum Rechtefehler, sonst der bisherige
 * Text (`sonst`, ab Werk `describeError`).
 */
export const rechtFehlerOder = (e: unknown, sonst: (e: unknown) => string = describeError): string => {
    const recht = typeof e === 'object' && e !== null
        ? rechtFehlerText(e as { code?: string; message?: string })
        : null;
    return recht ?? sonst(e);
};

/** Hinweis an einem Knopf, der wegen eines fehlenden Rechts gesperrt ist. */
export const rechtFehltHinweis = (recht: RechtSchluessel): string => `Recht „${rechtTitel(recht)}“ fehlt`;
